import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import '../../../css/GoogleScholar.css';

const normalizeVietnamese = (str) => {
    if (!str) return '';
    return str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/[^a-z0-9\s]/g, '').trim();
};

const calculateNameSimilarity = (nameA, nameB) => {
    if (!nameA || !nameB) return 0;

    const cleanAndSplit = (str) => {
        return str
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .replace(/[^a-z0-9]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .split(' ');
    };

    const tokensA = cleanAndSplit(nameA);
    const tokensB = cleanAndSplit(nameB);

    const exactMatches = tokensA.filter(token => tokensB.includes(token));
    if (exactMatches.length === 0) return 0;

    const remainingA = tokensA.filter(token => !exactMatches.includes(token));
    const remainingB = tokensB.filter(token => !exactMatches.includes(token));

    const initialsA = remainingA.map(token => token[0]).join('');
    const initialsB = remainingB.map(token => token[0]).join('');

    const combinedRemainingB = remainingB.join('');
    const combinedRemainingA = remainingA.join('');

    if (initialsA === combinedRemainingB || initialsB === combinedRemainingA || initialsA === initialsB) {
        return 0.95;
    }

    const maxTokens = Math.max(tokensA.length, tokensB.length);
    return exactMatches.length / maxTokens;
};

const findAuthorInArticle = (authorName, authorships) => {
    if (!authorships || !Array.isArray(authorships)) return null;

    let bestMatch = null;
    let bestSimilarity = 0;

    for (const authorship of authorships) {
        const namesToCheck = [
            authorship.raw_author_name,
            authorship.author_name,
        ].filter(Boolean);

        for (const name of namesToCheck) {
            const similarity = calculateNameSimilarity(authorName, name);

            if (similarity > bestSimilarity && similarity >= 0.5) {
                bestSimilarity = similarity;
                bestMatch = authorship;
            }
        }
    }

    return bestMatch;
};

const inferPositionFromAuthors = (authorName, authorsText) => {
    if (!authorName || !authorsText) return null;
    const parts = authorsText.split(/\s*,\s*/).map(a => a.trim()).filter(Boolean);
    if (!parts.length) return null; if (parts.length === 1) return 'first';

    let bestIdx = -1;
    let bestSim = 0;
    parts.forEach((name, idx) => {
        const sim = calculateNameSimilarity(authorName, name);
        if (sim > bestSim && sim >= 0.5) {
            bestSim = sim;
            bestIdx = idx;
        }
    });

    if (bestIdx === -1) return null;
    if (bestIdx === 0) return 'first';
    if (bestIdx === parts.length - 1) return 'last';
    return 'middle';
};

const getUserIdFromInput = (raw) => {
    if (!raw) return '';
    const trimmed = raw.trim();
    try {
        if (trimmed.startsWith('http')) {
            const url = new URL(trimmed);
            return url.searchParams.get('user')?.trim() || trimmed;
        }
    } catch (_) { }
    return trimmed;
};

const parseArticleDate = (dateString) => {
    if (!dateString) return null;
    const str = String(dateString).trim();
    const parts = str.split(/[-/]/);

    const year = parseInt(parts[0], 10);
    const month = parts[1] ? parseInt(parts[1], 10) - 1 : 0;
    const day = parts[2] ? parseInt(parts[2], 10) : 1;

    if (isNaN(year)) return null;
    return new Date(year, month, day);
};

const GoogleScholarModal = ({ isOpen, onClose, defaultLink }) => {
    const [userIdInput, setUserIdInput] = useState(defaultLink || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [author, setAuthor] = useState(null);
    const [articles, setArticles] = useState([]);

    const [isImporting, setIsImporting] = useState(false);

    const [filters, setFilters] = useState({ keyword: '', dateFrom: '', dateTo: '', doi: '', corresponding: '', role: '' });

    const [rankingDict, setRankingDict] = useState({});
    const [manualScores, setManualScores] = useState({});

    const [isDesktop, setIsDesktop] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getAuthHeaders = () => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    };

    useEffect(() => {
        setUserIdInput(defaultLink || '');
    }, [defaultLink]);

    useEffect(() => {
        if (isOpen) {
            const cachedDict = sessionStorage.getItem('scimagoRankingDict');

            if (cachedDict) {
                setRankingDict(JSON.parse(cachedDict));
                return;
            }

            const backendUrl = `${process.env.REACT_APP_API_BASE_URL}/scimagorankings`;

            fetch(backendUrl, { headers: getAuthHeaders() })
                .then(res => res.json())
                .then(data => {
                    if (Array.isArray(data)) {
                        const dict = {};
                        data.forEach(item => {
                            if (item.Title && item.Ranking) {
                                const cleanTitle = item.Title.toLowerCase().replace(/[^a-z0-9]/g, '');
                                dict[cleanTitle] = item.Ranking;
                            }
                        });
                        setRankingDict(dict);
                        sessionStorage.setItem('scimagoRankingDict', JSON.stringify(dict));
                    }
                })
                .catch(err => console.error("Lỗi tải dữ liệu Ranking:", err));
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && defaultLink) {
            const userId = getUserIdFromInput(defaultLink);
            if (!userId) {
                setError('Vui lòng cung cấp link hoặc User ID hợp lệ trong hồ sơ');
                return;
            }

            const fetchScholarData = async () => {
                setLoading(true); setError(''); setAuthor(null); setArticles([]); setManualScores({});
                try {
                    // const response = await fetch('http://localhost:5000/api/allpublications'); --> Đường dẫn chạy local
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/allpublications`, {
                        headers: getAuthHeaders()
                    });

                    if (!response.ok) throw new Error('Không thể tải dữ liệu từ máy chủ nội bộ (Lỗi 401 hoặc 500)');
                    const backendData = await response.json();

                    const userRecord = backendData.find(record => {
                        if (!record.Data) return false;

                        if (record.Data.articles && record.Data.articles.length > 0) {
                            const firstArticle = record.Data.articles[0];
                            if (firstArticle.citation_id && firstArticle.citation_id.startsWith(userId)) {
                                return true;
                            }
                        }

                        if (record.Data.author && record.Data.author.thumbnail) {
                            if (record.Data.author.thumbnail.includes(`user=${userId}`)) {
                                return true;
                            }
                        }

                        return false;
                    });

                    if (userRecord && userRecord.Data && userRecord.Data.author) {
                        setAuthor(userRecord.Data.author);
                        setArticles(userRecord.Data.articles || []);
                    } else {
                        throw new Error('Chưa có dữ liệu cho giảng viên này. Vui lòng chờ Hệ thống quét hoặc kiểm tra lại Link Scholar');
                    }
                } catch (err) {
                    setError(err.message);
                } finally {
                    setLoading(false);
                }
            };
            fetchScholarData();
        }
    }, [isOpen, defaultLink]);

    const computeArticleMeta = useCallback((article) => {
        const citations = article.cited_by?.value || 0;
        let authorPosition = null; let isCorresponding = false; let hasDoiData = false;

        if (article.doi && article.data?.authorships && author?.name) {
            const match = findAuthorInArticle(author.name, article.data.authorships);
            if (match) { authorPosition = match.author_position; isCorresponding = match.is_corresponding === true; hasDoiData = true; }
        }
        if (!hasDoiData && !authorPosition && article.authors && author?.name) {
            authorPosition = inferPositionFromAuthors(author.name, article.authors);
        }

        let totalAuthors = article.data?.authorships?.length || article.authors?.split(',').length || 0;
        let ranking = '-';
        let candidateNames = [];

        if (article.data?.primary_location?.source?.display_name) candidateNames.push(article.data.primary_location.source.display_name);
        if (article.data?.primary_location?.raw_source_name) candidateNames.push(article.data.primary_location.raw_source_name);

        if (article.publication) {
            let gsPub = article.publication.split(',')[0];
            candidateNames.push(gsPub);
            candidateNames.push(gsPub.replace(/\s+\d+.*$/, ''));
        }

        for (let name of candidateNames) {
            if (!name) continue;
            let cleanName = name.toLowerCase().replace(/[^a-z0-9]/g, '');
            if (rankingDict[cleanName]) {
                ranking = rankingDict[cleanName];
                break;
            }
        }

        if (ranking === '-' && article.publication) {
            let rawGsName = article.publication.toLowerCase().replace(/[^a-z0-9]/g, '');
            if (rawGsName.length > 10) {
                const sortedKeys = Object.keys(rankingDict).sort((a, b) => b.length - a.length);
                for (let scimagoName of sortedKeys) {
                    if (scimagoName.length > 10 && rawGsName.includes(scimagoName)) {
                        ranking = rankingDict[scimagoName];
                        break;
                    }
                }
            }
        }

        let diemQuyDoi = 0;

        const diemTapChi = (ranking !== '-')
            ? ranking
            : (manualScores[article.title] !== undefined ? manualScores[article.title] : (article.journal_score || ''));

        if (diemTapChi && authorPosition && totalAuthors > 0) {
            let multiplier = 0;
            const normalizedDiem = String(diemTapChi).toUpperCase().trim();

            if (normalizedDiem === 'Q1') multiplier = 3;
            else if (normalizedDiem === 'Q2') multiplier = 2;
            else if (normalizedDiem === 'Q3') multiplier = 1.5;
            else if (normalizedDiem === 'Q4' || normalizedDiem === 'SCOPUS') multiplier = 1;
            else {
                const num = parseFloat(diemTapChi);
                if (!isNaN(num)) multiplier = num;
            }

            if (multiplier > 0) {
                const posScore = authorPosition === 'first' ? (1 / 6) : 0;
                const corrScore = isCorresponding ? (1 / 6) : 0;
                const countScore = (2 / 3) / totalAuthors;
                let calculatedScore = (posScore + corrScore + countScore) * multiplier;
                diemQuyDoi = Math.round(calculatedScore * 100) / 100;
            }
        }

        return { citations, authorPosition, isCorresponding, hasDoiData, totalAuthors, ranking, diemQuyDoi };
    }, [author, rankingDict, manualScores]);

    const filteredArticles = useMemo(() => {
        let result = articles.map(a => ({ article: a, meta: computeArticleMeta(a) }));

        if (filters.keyword) {
            const kw = normalizeVietnamese(filters.keyword);
            result = result.filter(item => `${normalizeVietnamese(item.article.title)} ${normalizeVietnamese(item.article.authors)} ${normalizeVietnamese(item.article.publication)}`.includes(kw));
        }

        if (filters.dateFrom || filters.dateTo) {
            result = result.filter(item => {
                const dateStr = item.article.pub_date || item.article.publication_date || item.article.year;
                const articleDate = parseArticleDate(dateStr);

                if (!articleDate) return true;

                let isValid = true;

                if (filters.dateFrom) {
                    const fromDate = new Date(filters.dateFrom);
                    fromDate.setHours(0, 0, 0, 0);
                    isValid = isValid && (articleDate >= fromDate);
                }

                if (filters.dateTo) {
                    const toDate = new Date(filters.dateTo);
                    toDate.setHours(23, 59, 59, 999);
                    isValid = isValid && (articleDate <= toDate);
                }

                return isValid;
            });
        }

        if (filters.doi === 'doi') result = result.filter(item => item.article.doi);
        if (filters.doi === 'no-doi') result = result.filter(item => !item.article.doi);
        if (filters.corresponding === 'yes') result = result.filter(item => item.meta.isCorresponding);
        if (filters.corresponding === 'no') result = result.filter(item => !item.meta.isCorresponding);
        if (filters.role) result = result.filter(item => item.meta.authorPosition === filters.role);

        return result;
    }, [articles, filters, computeArticleMeta]);

    const stats = useMemo(() => {
        return filteredArticles.reduce((acc, curr) => {
            acc.citations += curr.meta.citations;
            if (curr.meta.authorPosition === 'first') acc.first++;
            if (curr.meta.isCorresponding) acc.corresponding++;
            acc.totalScore += (curr.meta.diemQuyDoi || 0);
            return acc;
        }, { citations: 0, first: 0, corresponding: 0, totalScore: 0 });
    }, [filteredArticles]);

    const handleImportToProfile = async () => {
        if (!articles || articles.length === 0) {
            setError('Không có bài báo nào để đồng bộ!');
            return;
        }

        setIsImporting(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scholarimport`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (response.ok) {
                alert("Đã đồng bộ thành công các bài báo từ Google Scholar vào hồ sơ kê khai (những bài đã tồn tại sẽ được bỏ qua)!");
                onClose();
                window.location.reload();
            } else {
                const errData = await response.json();
                alert("Lỗi khi đồng bộ: " + (errData.message || "Lỗi không xác định"));
            }
        } catch (error) {
            console.error("Lỗi đồng bộ Scholar:", error);
            alert("Lỗi kết nối máy chủ khi đồng bộ dữ liệu!");
        } finally {
            setIsImporting(false);
        }
    };

    const handleExport = () => {
        if (!filteredArticles.length) return;

        const rows = filteredArticles.map((item, index) => {
            const diemTapChi = (item.meta.ranking !== '-')
                ? item.meta.ranking
                : (manualScores[item.article.title] !== undefined ? manualScores[item.article.title] : (item.article.journal_score || ''));

            return {
                '#': index + 1,
                Title: item.article.title || '',
                Authors: item.article.authors || '',
                'Total Authors': item.meta.totalAuthors || '',
                Year: item.article.pub_date || item.article.publication_date || item.article.year || '',
                Publication: item.article.publication || '',
                'Điểm tạp chí/Kỷ yếu hội thảo': diemTapChi,
                Citations: item.meta.citations,
                DOI: item.article.doi || '',
                'Author Position': item.meta.authorPosition || '',
                Corresponding: item.meta.isCorresponding ? 'Yes' : 'No',
                'Điểm quy đổi': '',
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(rows);

        rows.forEach((_, idx) => {
            const excelRow = idx + 2;
            const formula = `IF(OR(G${excelRow}="", J${excelRow}="", K${excelRow}=""), 0, ROUND((IF(J${excelRow}="first", 1/6, 0) + IF(K${excelRow}="Yes", 1/6, 0) + (2/3)/D${excelRow}) * IF(G${excelRow}="Q1", 3, IF(G${excelRow}="Q2", 2, IF(G${excelRow}="Q3", 1.5, IF(OR(G${excelRow}="Q4", G${excelRow}="Scopus"), 1, G${excelRow})))), 2))`;
            worksheet[`L${excelRow}`] = { t: 'n', f: formula };
        });

        const lastDataRow = rows.length + 1;
        const totalRow = lastDataRow + 1;
        worksheet[`K${totalRow}`] = { t: 's', v: 'Tổng' };
        worksheet[`L${totalRow}`] = { t: 'n', f: `SUM(L2:L${lastDataRow})` };

        const noteRow = totalRow + 2;
        worksheet[`A${noteRow}`] = {
            t: 's',
            v: '1. Nhập dữ liệu tại Cột G (Điểm tạp chí/Kỷ yếu):\nBài báo quốc tế: Hệ thống đã tự động điền Q1, Q2, Q3, Q4. Nếu trống, hãy nhập Q1-Q4, Scopus hoặc điểm số.\nBài báo trong nước: Nhập trực tiếp số điểm.\n2. Hoàn thiện thông tin tác giả:\nCột J (Author Position): Nhập vị trí tác giả (first, middle hoặc last).\nCột K (Corresponding): Xác định tác giả liên hệ (Yes hoặc No).\nLưu ý: Sau khi nhập đủ, Cột L sẽ tự động tính điểm quy đổi.',
        };

        if (worksheet['!ref']) {
            const range = XLSX.utils.decode_range(worksheet['!ref']);
            range.e.r = Math.max(range.e.r, noteRow - 1);
            worksheet['!ref'] = XLSX.utils.encode_range(range);
        }

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Publications');

        const safeName = (author?.name || 'google_scholar').replace(/[^a-z0-9-_ ]/gi, '').trim().replace(/\s+/g, '_');
        XLSX.writeFile(workbook, `${safeName}_publications.xlsx`);
    };

    if (!isOpen) return null;

    const inputStyle = {
        width: '100%',
        padding: '10px 12px',
        borderRadius: '8px',
        border: '1px solid #cbd5e1',
        fontSize: '14px',
        outline: 'none',
        backgroundColor: '#fff',
        transition: 'border-color 0.2s',
        boxSizing: 'border-box'
    };

    const labelStyle = {
        fontSize: '13px',
        fontWeight: '600',
        color: '#475569',
        marginBottom: '6px',
        display: 'block'
    };

    const getRankingBadgeStyle = (ranking) => {
        const baseStyle = { padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', display: 'inline-block' };
        switch (ranking) {
            case 'Q1': return { ...baseStyle, backgroundColor: '#22c55e', color: 'white' };
            case 'Q2': return { ...baseStyle, backgroundColor: '#eab308', color: 'white' };
            case 'Q3': return { ...baseStyle, backgroundColor: '#f97316', color: 'white' };
            case 'Q4': return { ...baseStyle, backgroundColor: '#ef4444', color: 'white' };
            default: return { ...baseStyle, backgroundColor: '#f1f5f9', color: '#64748b', fontWeight: 'normal' };
        }
    };

    return (
        <div className="scholar-modal-overlay">
            <div className="scholar-modal-box">
                <button className="scholar-close-btn" onClick={onClose}>&times;</button>
                <div className="scholar-container">
                    <h1 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: isDesktop ? '' : '18px', paddingRight: isDesktop ? '' : '30px' }}>
                        Google Scholar Publications
                    </h1>

                    <div className="search-section" style={{ marginBottom: '20px' }}>
                        <label style={labelStyle}>Google Scholar Link hoặc User ID đang xử lý:</label>
                        <input
                            type="text"
                            value={userIdInput}
                            readOnly
                            style={{ ...inputStyle, backgroundColor: '#f8fafc', color: '#64748b', cursor: 'not-allowed' }}
                        />
                    </div>

                    {loading && <div className="spinner" style={{ margin: '20px auto' }}></div>}
                    {error && <div style={{ color: '#991b1b', background: '#fee2e2', padding: '14px', borderRadius: '12px', border: '1px solid #f87171', textAlign: 'center', marginBottom: '20px' }}>{error}</div>}

                    {author && (
                        <div className="author-info">
                            <div className="author-header" style={{ flexDirection: isDesktop ? 'row' : 'column', textAlign: isDesktop ? 'left' : 'center' }}>
                                <img src={author.thumbnail || 'https://via.placeholder.com/80'} alt="Avatar" className="author-avatar" />
                                <div>
                                    <h2>{author.name}</h2>
                                    <p style={{ color: '#64748b', fontSize: '14px' }}>{author.affiliations}</p>
                                    <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: isDesktop ? 'flex-start' : 'center' }}>
                                        {author.interests?.map(i => <span key={i.title} className="interest-tag">{i.title}</span>)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {articles.length > 0 && (
                        <div className="results-section">

                            <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', justifyContent: 'space-between', alignItems: isDesktop ? 'flex-start' : 'stretch', marginBottom: '30px', flexWrap: 'wrap', gap: '20px' }}>

                                <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', alignItems: isDesktop ? 'center' : 'stretch', gap: '15px' }}>
                                    <h3 style={{ margin: 0, fontSize: '20px', textAlign: isDesktop ? 'left' : 'center' }}>
                                        <i className="fa-regular fa-file-lines" style={{ marginRight: '8px' }}></i>
                                        Danh sách bài báo
                                    </h3>

                                    <button
                                        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#1a9953', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', color: '#fff', width: isDesktop ? 'auto' : '100%' }}
                                        onClick={handleImportToProfile}
                                        disabled={isImporting}
                                    >
                                        {isImporting ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-solid fa-cloud-arrow-down"></i>}
                                        {isImporting ? " Đang đồng bộ..." : " Đồng bộ vào Hồ sơ"}
                                    </button>

                                    <button
                                        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', color: '#0f172a', width: isDesktop ? 'auto' : '100%' }}
                                        onClick={handleExport}
                                    >
                                        <i className="fa-solid fa-download" style={{ fontSize: '14px', color: '#2563eb' }}></i>
                                        Xuất Excel
                                    </button>
                                </div>

                                <div className="stats" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: isDesktop ? 'flex-end' : 'center' }}>
                                    <div className="stat-item" style={{ background: '#fff', border: '1px solid #f1f5f9', boxShadow: '0 2px 5px rgba(0,0,0,0.03)', borderRadius: '12px', padding: '12px 24px', minWidth: '110px', flex: isDesktop ? 'none' : '1 1 45%' }}>
                                        <div className="number" style={{ fontSize: '24px', color: '#e20b0b', fontWeight: 'bold' }}>
                                            {Math.round(stats.totalScore * 100) / 100}
                                        </div>
                                        <div className="label" style={{ fontSize: '13px', color: '#64748b', fontWeight: 'bold' }}>Tổng Điểm Quy Đổi</div>
                                    </div>

                                    <div className="stat-item" style={{ background: '#fff', border: '1px solid #f1f5f9', boxShadow: '0 2px 5px rgba(0,0,0,0.03)', borderRadius: '12px', padding: '12px 24px', minWidth: '110px', flex: isDesktop ? 'none' : '1 1 45%' }}>
                                        <div className="number" style={{ fontSize: '24px', color: '#4f46e5', fontWeight: 'bold' }}>{filteredArticles.length}</div>
                                        <div className="label" style={{ fontSize: '13px', color: '#64748b' }}>Tổng bài báo</div>
                                    </div>
                                    <div className="stat-item" style={{ background: '#fff', border: '1px solid #f1f5f9', boxShadow: '0 2px 5px rgba(0,0,0,0.03)', borderRadius: '12px', padding: '12px 24px', minWidth: '110px', flex: isDesktop ? 'none' : '1 1 45%' }}>
                                        <div className="number" style={{ fontSize: '24px', color: '#4f46e5', fontWeight: 'bold' }}>{stats.citations}</div>
                                        <div className="label" style={{ fontSize: '13px', color: '#64748b' }}>Tổng trích dẫn</div>
                                    </div>
                                    <div className="stat-item" style={{ background: '#fff', border: '1px solid #f1f5f9', boxShadow: '0 2px 5px rgba(0,0,0,0.03)', borderRadius: '12px', padding: '12px 24px', minWidth: '110px', flex: isDesktop ? 'none' : '1 1 45%' }}>
                                        <div className="number" style={{ fontSize: '24px', color: '#4f46e5', fontWeight: 'bold' }}>{stats.first}</div>
                                        <div className="label" style={{ fontSize: '13px', color: '#64748b' }}>First Author</div>
                                    </div>
                                    <div className="stat-item" style={{ background: '#fff', border: '1px solid #f1f5f9', boxShadow: '0 2px 5px rgba(0,0,0,0.03)', borderRadius: '12px', padding: '12px 24px', minWidth: '110px', flex: isDesktop ? 'none' : '1 1 45%' }}>
                                        <div className="number" style={{ fontSize: '24px', color: '#4f46e5', fontWeight: 'bold' }}>{stats.corresponding}</div>
                                        <div className="label" style={{ fontSize: '13px', color: '#64748b' }}>Corresponding</div>
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: '25px' }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: isDesktop ? '2fr 1fr 1fr 1.2fr 1.2fr 1.2fr' : '1fr',
                                    gap: '15px',
                                    alignItems: 'end'
                                }}>
                                    <div>
                                        <label style={labelStyle}>Từ khóa</label>
                                        <input style={inputStyle} placeholder="Tiêu đề, tác giả, tạp chí" onChange={e => setFilters({ ...filters, keyword: e.target.value })} value={filters.keyword} />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Từ ngày</label>
                                        <input type="date" style={inputStyle} onChange={e => setFilters({ ...filters, dateFrom: e.target.value })} value={filters.dateFrom} />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Đến ngày</label>
                                        <input type="date" style={inputStyle} onChange={e => setFilters({ ...filters, dateTo: e.target.value })} value={filters.dateTo} />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>DOI</label>
                                        <select style={inputStyle} onChange={e => setFilters({ ...filters, doi: e.target.value })} value={filters.doi}>
                                            <option value="">Tất cả</option><option value="doi">Có DOI</option><option value="no-doi">Không DOI</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Corresponding</label>
                                        <select style={inputStyle} onChange={e => setFilters({ ...filters, corresponding: e.target.value })} value={filters.corresponding}>
                                            <option value="">Tất cả</option><option value="yes">Có</option><option value="no">Không</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Vị trí tác giả</label>
                                        <select style={inputStyle} onChange={e => setFilters({ ...filters, role: e.target.value })} value={filters.role}>
                                            <option value="">Tất cả</option><option value="first">First</option><option value="middle">Middle</option><option value="last">Last</option>
                                        </select>
                                    </div>
                                </div>
                                <button
                                    style={{ marginTop: '15px', padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', width: isDesktop ? 'auto' : '100%' }}
                                    onClick={() => setFilters({ keyword: '', dateFrom: '', dateTo: '', doi: '', corresponding: '', role: '' })}
                                >
                                    Xóa lọc
                                </button>
                            </div>

                            <div className="table-container" style={{ overflowX: 'auto', width: isDesktop ? 'auto' : '100%' }}>
                                <table className="scholar-table" style={{ minWidth: '1300px' }}>
                                    <thead>
                                        <tr>
                                            <th>STT</th>
                                            <th>Tiêu đề</th>
                                            <th>Tác giả</th>
                                            <th style={{ textAlign: 'center' }}>Số tác giả</th>
                                            <th style={{ textAlign: 'center' }}>Ngày XB</th>
                                            <th>Tạp chí/Hội nghị</th>
                                            <th style={{ textAlign: 'center' }}>Điểm tạp chí/Kỷ yếu</th>
                                            <th style={{ textAlign: 'center' }}>Trích dẫn</th>
                                            <th>DOI</th>
                                            <th style={{ textAlign: 'center' }}>Author Position</th>
                                            <th style={{ textAlign: 'center' }}>Corresponding</th>
                                            <th style={{ textAlign: 'center', backgroundColor: '#fef2f2', color: '#991b1b' }}>Điểm quy đổi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredArticles.length === 0 ? (
                                            <tr>
                                                <td colSpan="12" style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>Không có bài báo phù hợp với bộ lọc</td>
                                            </tr>
                                        ) : (
                                            filteredArticles.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{index + 1}</td>
                                                    <td style={{ maxWidth: '280px' }}>
                                                        <a href={item.article.link} target="_blank" rel="noreferrer" style={{ color: '#4f46e5', fontWeight: 'bold', textDecoration: 'none' }}>{item.article.title}</a>
                                                    </td>
                                                    <td style={{ fontSize: '13px', color: '#64748b', maxWidth: '180px' }}>{item.article.authors || 'N/A'}</td>
                                                    <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#0f172a' }}>{item.meta.totalAuthors || '-'}</td>
                                                    <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#0f172a' }}>{item.article.pub_date || item.article.publication_date || item.article.year || 'N/A'}</td>
                                                    <td style={{ fontSize: '13px', color: '#64748b', maxWidth: '200px' }}>{item.article.publication || 'N/A'}</td>

                                                    <td style={{ textAlign: 'center' }}>
                                                        {item.meta.ranking !== '-' ? (
                                                            <span style={getRankingBadgeStyle(item.meta.ranking)}>{item.meta.ranking}</span>
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                placeholder="Nhập điểm"
                                                                value={manualScores[item.article.title] !== undefined ? manualScores[item.article.title] : (item.article.journal_score || '')}
                                                                onChange={(e) => setManualScores(prev => ({ ...prev, [item.article.title]: e.target.value }))}
                                                                style={{ width: '70px', padding: '4px', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '13px', outline: 'none' }}
                                                            />
                                                        )}
                                                    </td>

                                                    <td style={{ textAlign: 'center' }}>
                                                        <span className={`citation-badge ${item.meta.citations === 0 ? 'zero' : ''}`}>{item.meta.citations}</span>
                                                    </td>
                                                    <td style={{ fontSize: '12px' }}>
                                                        {item.article.doi ? (
                                                            <a href={`https://doi.org/${item.article.doi}`} target="_blank" rel="noreferrer" style={{ color: '#4f46e5', textDecoration: 'none', fontWeight: 'bold' }}>{item.article.doi}</a>
                                                        ) : (
                                                            <span style={{ color: '#999', fontStyle: 'italic' }}>Không có</span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {item.meta.authorPosition ? (
                                                            <span className={`role-badge ${item.meta.authorPosition}`}>{item.meta.authorPosition.charAt(0).toUpperCase() + item.meta.authorPosition.slice(1)}</span>
                                                        ) : (
                                                            <span style={{ color: '#999' }}>{item.article.doi ? 'N/A' : '-'}</span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {item.article.doi ? (
                                                            item.meta.hasDoiData ? (
                                                                item.meta.isCorresponding ? (
                                                                    <span className="role-badge corresponding">✓ Yes</span>
                                                                ) : (
                                                                    <span className="role-badge none">✗ No</span>
                                                                )
                                                            ) : <span style={{ color: '#999' }}>N/A</span>
                                                        ) : <span style={{ color: '#999' }}>-</span>}
                                                    </td>
                                                    <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#b91c1c', backgroundColor: '#fef2f2' }}>
                                                        {item.meta.diemQuyDoi > 0 ? item.meta.diemQuyDoi : '-'}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                    {filteredArticles.length > 0 && (
                                        <tfoot>
                                            <tr style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #cbd5e1' }}>
                                                <td colSpan="11" style={{ textAlign: 'right', fontWeight: 'bold', paddingRight: '20px', fontSize: '15px' }}>
                                                    TỔNG CỘNG:
                                                </td>
                                                <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#e20b0b', fontSize: '18px', backgroundColor: '#fee2e2' }}>
                                                    {Math.round(stats.totalScore * 100) / 100}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>

                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GoogleScholarModal;